import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from "vue"
import { TranslateResult } from "vue-i18n"
import Draggable, { MoveEvent } from "vuedraggable"

import i18n from "@/i18n"
import { getFullImagePath } from "@/utils/cdn"

import ImageCropper from "./ImageCropper.vue"

type ImageRatio = {
  [key: string]: number
}
type ImageCropData = {
  index: number
  file: File
}
type ImageLoaderProps = {
  ratio?: ImageRatio
  images?: Array<{ filename: string; on_cdn: boolean } | string | File>
  limit?: number
  loading?: boolean
  maxKb: number
}
type Emits = {
  (event: "removeImage", index: number): void
  (event: "addImage", file: File): void
  (event: "sortImages", sortEvent: any): void
  (event: "changeImage", changeData: ImageCropData)
}
type SortableMoveEvent = {
  oldIndex: number
  newIndex: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ImagesLoader',
  props: {
    ratio: null,
    images: null,
    limit: null,
    loading: { type: Boolean },
    maxKb: null
  },
  emits: ["removeImage", "addImage", "sortImages", "changeImage"],
  setup(__props: any, { emit }: { emit: ({
  (event: "removeImage", index: number): void
  (event: "addImage", file: File): void
  (event: "sortImages", sortEvent: any): void
  (event: "changeImage", changeData: ImageCropData)
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  ratio?: ImageRatio
  images?: Array<{ filename: string; on_cdn: boolean } | string | File>
  limit?: number
  loading?: boolean
  maxKb: number
};




const fileInputRef = ref<HTMLInputElement>()

const showMinResolutionRequirement = computed(() =>
  Boolean(props.ratio?.minHeight && props.ratio?.minWidth)
)

// CHOOSE
const showUpload = computed<boolean>(() => props.images.length >= props.limit)

const onChooseFiles = () => {
  fileInputRef.value.click()
}

// VALIDATIONS
const allowedFormats = ".jpg, .jpeg, .png"
const fileTypes = ["image/jpeg", "image/pjpeg", "image/png"]

const isValidFormat = (file: File): boolean => {
  return fileTypes.includes(file.type)
}
const isValidSize = (file: File): boolean => {
  return Boolean(file.size / 1024 < +props.maxKb)
}
const isValidRatio = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    const { maxWidth = Infinity, maxHeight = Infinity, minWidth = 0, minHeight = 0 } = props.ratio

    reader.onload = () => {
      const img = new Image()

      img.onload = () => {
        const { width, height } = img

        if (width < minWidth || width > maxWidth || height < minHeight || height > maxHeight) {
          reject()
        } else {
          resolve(file)
        }
      }

      img.src = reader.result as string
    }

    reader.readAsDataURL(file)
  })
}
const getFormat = (filename: string): string => {
  return filename.split(".").pop()
}

// ERRORS
const errorMessage = ref<TranslateResult>("")

const errors = {
  limit: i18n.tc("common.max_file_load", +props.limit),
  formats: `${i18n.t("common.invalid_data")} ${allowedFormats}`,
  ratio: `${i18n.t("common.invalid_ratio")}`,
  size: i18n.tc("common.max_file_size", +(props.maxKb / 1024).toFixed(2))
}

const showError = (type) => {
  errorMessage.value = errors[type]
}

// PREVIEW
const getImageSrc = (path: string | File | { filename: string; on_cdn: boolean }) => {
  if (typeof path === "string") {
    return path.startsWith("http") ? path : getFullImagePath(path, "full")
  }

  if (path instanceof File) {
    return URL.createObjectURL(path)
  }

  return getFullImagePath(path.filename, "full")
}

// ADD
const createReadPromise = (file: File): Promise<File | undefined> => {
  return new Promise(async (resolve) => {
    if (!isValidSize(file)) {
      showError("size")
      return resolve(undefined)
    }

    if (!isValidFormat(file)) {
      showError("format")
      return resolve(undefined)
    }

    if (props.ratio) {
      try {
        await isValidRatio(file)
      } catch (err) {
        showError("ratio")
        return resolve(undefined)
      }
    }

    resolve(file)
  })
}
const addFiles = async (event: InputEvent): Promise<void> => {
  const { files: selectedFiles } = event.target as HTMLInputElement

  if (props.images.length + selectedFiles.length > props.limit) {
    showError("limit")
    return
  }

  errorMessage.value = ""

  for (let i = 0; i < selectedFiles.length; i++) {
    const image = await createReadPromise(selectedFiles[i])

    if (!image) continue

    emit("addImage", image)
  }

  fileInputRef.value.value = null
}

// SORT
const onSortImages = (sortEvent: SortableMoveEvent & MoveEvent<HTMLDivElement>) => {
  if (sortEvent.oldIndex !== sortEvent.newIndex) {
    emit("sortImages", sortEvent)
  }
}

// REMOVE
const removeFileByIndex = (index: number) => {
  if (index < 0) return

  emit("removeImage", index)
}

// CROP
const editedImage = ref<any>()
const cropModalVisible = ref<boolean>(false)

const onOpenCropModal = (index: number) => {
  const image = props.images[index]

  if (image instanceof File) {
    editedImage.value = {
      preview: getImageSrc(image),
      filename: image.name,
      format: image.type.split("/").at(-1)
    }
  } else if (typeof image === "string") {
    editedImage.value = { preview: getImageSrc(image), filename: image, format: getFormat(image) }
  } else {
    editedImage.value = { ...image, preview: getImageSrc(image), format: getFormat(image.filename) }
  }

  cropModalVisible.value = true
}
const handleCropImage = async (file: File) => {
  const index = props.images.findIndex((item) => {
    if (typeof item === "string") {
      return item === editedImage.value.filename
    }

    if (item instanceof File) {
      return item.name === editedImage.value.filename
    }

    return item.filename === editedImage.value.filename
  })

  emit("changeImage", { index, file })
  onCloseCropModal()
}

// CLOSE
const onCloseCropModal = () => {
  cropModalVisible.value = false
  editedImage.value = undefined
}

return { __sfc: true,props, emit, fileInputRef, showMinResolutionRequirement, showUpload, onChooseFiles, allowedFormats, fileTypes, isValidFormat, isValidSize, isValidRatio, getFormat, errorMessage, errors, showError, getImageSrc, createReadPromise, addFiles, onSortImages, removeFileByIndex, editedImage, cropModalVisible, onOpenCropModal, handleCropImage, onCloseCropModal, Draggable, ImageCropper }
}

})